import React from "react";
import NavBar from "../../components/navbar";
import BaseListPage from "../../base/BaseListPage";
import {
  countObjectUseCase,
  findObjectUseCase,
  getObjectUseCase,
  upsertUseCase,
} from "../../usecases/object";
import { updateSchemaUseCase } from "../../usecases/schema/usecases";
import EmpResignationPagePresenter from "./EmpResignationPagePresenter";
import ResignationFormModal from "../../components/FormModalEmp";
import { format } from "date-fns";
import { dialog, printComponent } from "../../nq-component";
import ClearancePrint from "./components/ClearancePrint";
import QuitclaimPrint from "./components/QuitclaimPrint";
import DialogSuccess from "./components/DialogSuccess";
import AddButton from "./components/AddButton";

class EmpResignationPage extends BaseListPage {
  constructor(props) {
    super(props);
    this.presenter = new EmpResignationPagePresenter(
      this,
      findObjectUseCase(),
      countObjectUseCase(),
      upsertUseCase(),
      updateSchemaUseCase(),
      getObjectUseCase()
    );
    this.state = {
      objects: [],
      selected: [],
      progress: true,
      count: 0,
      where: {},
      show: false,
      submitting: false,
      animationActive: false,
      userDetails: {},
    };
    this.clearancePDF = React.createRef();
    this.quitclaimPDF = React.createRef();
    this.pollingInterval = null;
    this.POLLING_INTERVAL_MS = 1000;
  }

  onClickPrint = () => this.presenter.onClickPrint();

  onClickPrintQuitclaim = () => this.exportQuitclaimPDF();

  exportPDF = () => {
    this.clearancePDF.current
      ? printComponent(this.clearancePDF.current)
      : this.showMessage("Error generating PDF. Please try again.", true);
  };

  exportQuitclaimPDF = () => {
    this.quitclaimPDF.current
      ? printComponent(this.quitclaimPDF.current)
      : this.showMessage(
          "Error generating quitclaim PDF. Please try again.",
          true
        );
  };

  componentDidMount() {
    super.componentDidMount();
    this.startRealtimePolling();
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
    if (typeof window !== "undefined" && window.bootstrap) {
      const tooltipTriggerList = [].slice.call(
        document.querySelectorAll('[data-bs-toggle="tooltip"]')
      );
      tooltipTriggerList.map(function (tooltipTriggerEl) {
        return new window.bootstrap.Tooltip(tooltipTriggerEl);
      });
    }
  }

  componentWillUnmount() {
    this.stopRealtimePolling();
    document.removeEventListener(
      "visibilitychange",
      this.handleVisibilityChange
    );
  }

  componentDidUpdate() {
    if (typeof window !== "undefined" && window.bootstrap) {
      const tooltipTriggerList = [].slice.call(
        document.querySelectorAll('[data-bs-toggle="tooltip"]')
      );
      tooltipTriggerList.map(function (tooltipTriggerEl) {
        return new window.bootstrap.Tooltip(tooltipTriggerEl);
      });
    }
  }

  handleVisibilityChange = () => {
    if (document.visibilityState === "visible") {
      this.presenter.fetchResignations();
      this.stopRealtimePolling();
      this.startRealtimePolling();
    }
  };

  startRealtimePolling = () => {
    this.stopRealtimePolling();
    // Immediately fetch data before starting the interval
    document.visibilityState === "visible" &&
      this.presenter.fetchResignations(true);

    this.pollingInterval = setInterval(() => {
      document.visibilityState === "visible" &&
        this.presenter.fetchResignations(true);
    }, this.POLLING_INTERVAL_MS);
  };

  stopRealtimePolling = () => {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  };

  successModal = (message) =>
    dialog.fire({
      html: <DialogSuccess message={message} />,
      footer: false,
    });

  handleShow = () => this.setState({ show: true });
  handleClose = () => this.setState({ show: false });
  getCollectionName = () => "resignation";
  handleSubmitResignation = (formData) =>
    this.presenter.submitResignation(formData);
  showMessage = (message, isError = false) => {
    window.toast
      ? isError
        ? window.toast.error(message)
        : window.toast.success(message)
      : alert(isError ? `Error: ${message}` : message);
  };
  setSubmitting = (isSubmitting) => this.setState({ submitting: isSubmitting });
  closeForm = () => this.setState({ show: false });
  handlePrintClearanceForm = () =>
    this.showMessage("Generating clearance form...");

  formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "MMM. dd, yyyy");
    } catch (error) {
      console.error("Error formatting date:", error);
      return dateString;
    }
  };

  getStatusColor = (status) => {
    const statusMap = {
      approved: "text-success",
      pending: "text-warning",
      "in progress": "text-info",
      disapproved: "text-danger",
    };
    return statusMap[status?.toLowerCase()] || "text-secondary";
  };

  getFullName = (employee) => {
    if (!employee) return "N/A";

    const firstName = employee.firstName || "";
    const middleName = employee.middleName || "";
    const lastName = employee.lastName || "";

    return (
      [firstName, middleName, lastName].filter(Boolean).join(" ").trim() ||
      "N/A"
    );
  };

  toExitInterview() {
    const { userDetails } = this.state;

    const id = userDetails?.exit_interview?.id;

    if (id) {
      this.navigateTo(`/exit-form/${id}`);
    } else {
      this.navigateTo(`/exit-form`);
    }
  }

  renderResignationCard = (resignation) => {
    if (!resignation) return null;

    const submittedDate = resignation.created
      ? new Date(resignation.created)
      : new Date();

    // Get the employee's full name
    const employeeFullName = this.getFullName(resignation.employee);

    // Check if the employee has lastPayDate in the employee collection
    const hasLastPayDate =
      resignation.employee &&
      resignation.employee.lastPayDate &&
      resignation.employee.lastPayDate !== "";

    const iconStyle = { color: "#D18237", fontSize: "26px" };
    const circleStyle = {
      backgroundColor: "#F5F5F5",
      width: "50px",
      height: "50px",
      flexShrink: 0,
    };

    const renderInfoItem = (icon, label, value, statusClass = "") => (
      <div className="d-flex align-items-center mb-4">
        <div
          className="d-flex align-items-center justify-content-center rounded-circle me-3"
          style={circleStyle}
        >
          <i className={`bi bi-${icon}`} style={iconStyle}></i>
        </div>
        <div>
          <div className="fw-bold small">{label}</div>
          <div className={`small ${statusClass}`}>{value}</div>
        </div>
      </div>
    );

    return (
      <div
        className={`card border-0 shadow-sm mb-4 fade-in ${
          this.state.animationActive ? "active" : ""
        }`}
        style={{ maxWidth: "450px" }}
      >
        <div className="card-body p-4">
          {/* Employee Name */}
          {renderInfoItem("person-circle", "Employee", employeeFullName)}
          {renderInfoItem(
            "calendar-event",
            "Date Submitted",
            this.formatDate(submittedDate)
          )}
          {renderInfoItem(
            "three-dots",
            "Status",
            resignation.status || "Pending",
            this.getStatusColor(resignation.status)
          )}
          {renderInfoItem(
            "calendar-check",
            "Effective Date",
            this.formatDate(resignation.effectiveDate)
          )}
          {hasLastPayDate &&
            renderInfoItem(
              "cash-coin",
              "Last Pay Date",
              this.formatDate(resignation.employee.lastPayDate)
            )}
          <div className="d-flex flex-column gap-2">
            <div className="d-flex gap-2">
              <button
                className="btn text-white mt-2 py-2 flex-grow-1"
                style={{ backgroundColor: "#E09552", fontSize: "13px" }}
                onClick={this.onClickPrint}
                // Removed the disabled={!isApproved} check so button is always enabled
              >
                <i
                  className="bi bi-printer"
                  style={{
                    fontSize: "16px",
                    marginLeft: "4px",
                    marginRight: "12px",
                  }}
                ></i>
                Print Clearance Form
              </button>
              <button
                className="btn text-white mt-2 py-2 flex-grow-1"
                style={{ backgroundColor: "#E09552", fontSize: "13px" }}
                onClick={this.onClickPrintQuitclaim}
                disabled={!hasLastPayDate}
              >
                <i
                  className="bi bi-file-earmark-text"
                  style={{
                    fontSize: "16px",
                    marginLeft: "4px",
                    marginRight: "12px",
                  }}
                ></i>
                Print Quitclaim
              </button>
            </div>

            <button
              className="btn text-white"
              style={{ backgroundColor: "#3174ad" }}
              onClick={this.toExitInterview.bind(this)}
            >
              <i className="bi bi-file-earmark-text me-2 fw-bold" />
              Exit Interview Form
            </button>
          </div>
        </div>
      </div>
    );
  };

  render() {
    const { objects, show, submitting, progress, userDetails } = this.state;
    const currentUser = this.getCurrentUser();
    const userResignation = objects.length > 0 ? objects[0] : null;
    const animationClass = this.state.animationActive ? "active" : "";

    const floatingButtonStyle = {
      position: "fixed",
      bottom: "20px",
      right: "20px",
      backgroundColor: "#E09552",
      color: "white",
      border: "none",
      width: "60px",
      height: "60px",
      borderRadius: "50%",
      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "24px",
      cursor: "pointer",
      transition: "transform 0.3s ease, background-color 0.3s ease",
    };
    const user = this.getCurrentUser();

    console.warn("objects", objects[0]);
    console.warn("userDetails", userDetails);
    return (
      <>
        <div className="d-none">
          <div ref={this.clearancePDF}>
            <ClearancePrint
              object={userResignation}
              currentUser={currentUser}
            />
          </div>
          <div ref={this.quitclaimPDF}>
            <QuitclaimPrint
              object={userResignation}
              currentUser={currentUser}
            />
          </div>
        </div>
        <NavBar user={user} />
        <style>
          {`
            @keyframes fadeIn {
              from { opacity: 0; transform: translateY(20px); }
              to { opacity: 1; transform: translateY(0); }
            }
            
            @keyframes pulse {
              0% { transform: scale(1); }
              50% { transform: scale(1.05); }
              100% { transform: scale(1); }
            }
            
            .fade-in {
              opacity: 0;
              transform: translateY(20px);
            }
            
            .fade-in.active {
              opacity: 1;
              transform: translateY(0);
              transition: opacity 0.6s ease-out, transform 0.6s ease-out;
            }
          `}
        </style>
        <div className="p-3 p-lg-4 overflow-auto">
          <div className="d-flex justify-content-between">
            <h4 className={`fw-bold title-animation ${animationClass}`}>
              Resignation
            </h4>
            {!userDetails?.exit_interview && (
              <button
                className="btn text-white"
                style={{ backgroundColor: "#3174ad" }}
                onClick={this.toExitInterview.bind(this)}
              >
                <i className="bi bi-file-earmark-text me-2 fw-bold" />
                Exit Interview Form
              </button>
            )}
          </div>

          {progress ? (
            <div className="text-center py-5">
              <div
                className="spinner-border"
                style={{ color: "#E09552" }}
                role="status"
              >
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : objects.length > 0 ? (
            <div className="container-fluid px-0">
              <div className="row">
                <div className="col-lg-5 col-md-6">
                  {this.renderResignationCard(userResignation)}
                </div>
              </div>
            </div>
          ) : (
            <div
              className={`text-center text-muted py-5 fade-in ${animationClass}`}
            >
              <div className="mb-3">
                <i className="bi bi-file-earmark-x fs-1"></i>
              </div>
              <p className="mb-1">No resignation requests found</p>
              <p className="small">
                Click the "+" button to submit a resignation request
              </p>
            </div>
          )}

          {!progress && objects.length === 0 && (
            <AddButton
              objects={userDetails}
              handleShow={this.handleShow.bind(this)}
              floatingButtonStyle={floatingButtonStyle}
              animationClass={animationClass}
            />
          )}
        </div>
        <ResignationFormModal
          show={show}
          onClose={this.handleClose}
          onSubmit={this.handleSubmitResignation}
          currentUser={currentUser}
          isSubmitting={submitting}
        />
      </>
    );
  }
}

export default EmpResignationPage;
